import { SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { message, Table, Icon } from 'antd';
import classNames from 'classnames';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TableSearchInput, TableAsyncSelect } from 'common/components/table-filter-component';
import { PicEmptyTransfer as PicEmptyTransferIcon } from 'common/components/svgs';
import {
  QUERY_PARTNER_TRANSACTION,
  QUERY_LIST_PARTNERS,
} from '~pages/dashboard/referral/transfer/queries';
import { dayFormatType, formatDay } from '~helpers/day';
import BulkOperationResultContainer from '~components/operation-result-modal';
import { downloadExcelFile } from './utils';
import TransferEditorContainer from './transfer-editor/index';
import BulkUpdate from './transfer-upload';
import { formatPriceWithSpace } from '~helpers/currency';
import { PartnerPayoutRecordStage, PartnerTransactionStatus } from '~constants/referral';

/**
 *
 * @param {string|null|undefined} text need trim text.
 * @returns {string|null}
 */
const removeSpace = text => {
  if (text) {
    return text.trim() || null;
  }
  return text;
};

const Transfer = () => {
  const { t } = useTranslation();
  const transferPromiseFunc = useRef(null);

  const [selectedEditorType, setSelectEditorType] = useState('edit');

  const [showPartnerNameFilter, setShowPartnerNameFilter] = useState(false);
  const [showPartnerCodeFilter, setShowPartnerCodeFilter] = useState(false);
  const [showOppFilter, setShowOppFilter] = useState(false);

  const [showTransferEditor, setShowTransferEditor] = useState(false);
  const [showTransferResult, setShowTransferResult] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const [queryFilter, setQueryFilter] = useState({
    partnerName: null,
    partnerCode: null,
    partnerIds: [],
    bookingNumbers: null,
  });

  const isValidFilter = useMemo(() => {
    return (
      queryFilter.partnerCode ||
      queryFilter.partnerName ||
      queryFilter.partnerIds?.length ||
      queryFilter.bookingNumbers
    );
  }, [queryFilter]);

  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
  });

  const { loading, data: transactionListResponse } = useQuery(QUERY_PARTNER_TRANSACTION, {
    variables: {
      pageNumber: pagination.pageNumber,
      pageSize: pagination.pageSize,
      sortBy: 'UPDATED_AT',
      sortDirection: 'DESCENDING',
      subTypes: ['TRANSFER'],
      partnerIds: queryFilter.partnerIds,
      bookingNumbers: queryFilter.bookingNumbers ? [queryFilter.bookingNumbers] : null,
    },
    fetchPolicy: 'no-cache',
    onError: e => {
      message.error(e?.toString());
    },
  });

  const transactionList = useMemo(() => {
    return transactionListResponse?.listPartnerTransaction?.edges.map(edge => edge.node) || [];
  }, [transactionListResponse]);

  const listTotalCount = useMemo(() => {
    return transactionListResponse?.listPartnerTransaction?.totalCount || 0;
  }, [transactionListResponse]);

  const { loading: partnerListLoading, data: partnerListResponse } = useQuery(QUERY_LIST_PARTNERS, {
    variables: {
      name: queryFilter.partnerName || null,
      partnerCode: queryFilter.partnerCode || null,
    },
  });

  const partnerList = useMemo(() => {
    return (
      partnerListResponse?.listPartners?.edges.map(edge => ({
        label: edge.node.name,
        value: edge.node.id,
      })) || []
    );
  }, [partnerListResponse]);

  const handleAddNewTransfer = () => {
    setSelectEditorType('edit');
    setShowTransferEditor(true);
  };

  const handleCloseEditorModal = () => {
    setShowTransferEditor(false);
  };

  const tableColumns = [
    {
      title: t('cms.referral.partner.list.column.name'),
      dataIndex: 'partner.name',
      key: 'partnerName',
      className: 'transfer-page__body__list__column',
      width: 150,
      fixed: 'left',
      render: text => (
        <span title={text} className="transfer-page__body__list__column-body">
          {text || '-'}
        </span>
      ),
      filterDropdown: (
        <div className="partner__body__list__columns-dropdown">
          <TableAsyncSelect
            field="partnerIds"
            value={queryFilter.partnerIds ? [queryFilter.partnerIds] : []}
            list={partnerList}
            onInput={value =>
              setQueryFilter(current => ({ ...current, partnerName: value, partnerCode: '' }))
            }
            fetching={partnerListLoading}
            onChange={(_fieldName, value) => {
              const partnerIds = value.length ? value[0] : null;
              setQueryFilter(current => ({ ...current, partnerIds: partnerIds }));
              setPagination(current => ({ ...current, pageNumber: 1 }));
              setShowPartnerNameFilter(false);
            }}
          />
        </div>
      ),
      filterIcon: (
        <SearchOutlined style={{ color: queryFilter.partnerName ? '#38b2a6' : '#c8c9cb' }} />
      ),
      filterDropdownVisible: showPartnerNameFilter,
      onFilterDropdownVisibleChange: view => {
        setShowPartnerNameFilter(view);
      },
    },
    {
      title: (
        <div className="transfer-page__body__list__column-header">
          {t('cms.referral.partner.list.column.partner_code')}
        </div>
      ),
      dataIndex: 'partner.partnerCode',
      key: 'partnerCode',
      className: 'transfer-page__body__list__column',
      width: '12%',
      render: text => <span className="transfer-page__body__list__column-body">{text || '-'}</span>,
      filterDropdown: (
        <TableAsyncSelect
          field="partnerIds"
          value={queryFilter.partnerIds ? [queryFilter.partnerIds] : []}
          list={partnerList}
          onInput={value =>
            setQueryFilter(current => ({ ...current, partnerName: '', partnerCode: value }))
          }
          fetching={partnerListLoading}
          onChange={(_fieldName, value) => {
            const partnerIds = value.length ? value[0] : null;
            setQueryFilter(current => ({ ...current, partnerIds: partnerIds }));
            setPagination(current => ({ ...current, pageNumber: 1 }));
            setShowPartnerCodeFilter(false);
          }}
        />
      ),
      filterIcon: (
        <SearchOutlined style={{ color: queryFilter.partnerCode ? '#38b2a6' : '#c8c9cb' }} />
      ),
      filterDropdownVisible: showPartnerCodeFilter,
      onFilterDropdownVisibleChange: view => {
        setShowPartnerCodeFilter(view);
      },
    },
    {
      title: (
        <div className="transfer-page__body__list__column-header">
          {t('cms.referral.transfer.list.column.amount')}
        </div>
      ),
      dataIndex: 'amount',
      key: 'amount',
      width: '11%',
      className: 'transfer-page__body__list__column',
      render: (text, record) => (
        <span className="transfer-page__body__list__column-body">
          {text ? formatPriceWithSpace(text, record.currency) : '-'}
        </span>
      ),
    },
    {
      title: (
        <div className="transfer-page__body__list__column-header">
          {t('cms.referral.payout.list.column.oppos_no')}
        </div>
      ),
      dataIndex: 'bookingNumber',
      key: 'bookingNumber',
      className: 'transfer-page__body__list__column',
      render: text => <span className="transfer-page__body__list__column-body">{text || '-'}</span>,
      filterDropdown: (
        <div className="partner__body__list__columns-dropdown">
          <TableSearchInput
            field="bookingNumber"
            value={queryFilter.bookingNumbers || ''}
            onChange={(_fieldName, value) => {
              setQueryFilter(current => ({ ...current, bookingNumbers: removeSpace(value) }));
              setPagination(current => ({ ...current, pageNumber: 1 }));
              setShowOppFilter(false);
            }}
          />
        </div>
      ),
      filterIcon: (
        <SearchOutlined style={{ color: queryFilter.bookingNumbers ? '#38b2a6' : '#c8c9cb' }} />
      ),
      filterDropdownVisible: showOppFilter,
      onFilterDropdownVisibleChange: view => {
        setShowOppFilter(view);
      },
    },
    {
      title: (
        <div className="transfer-page__body__list__column-header">
          {t('cms.referral.transfer.list.column.passport_first_name')}
        </div>
      ),
      dataIndex: 'description.passportFirstName',
      key: 'passportFirstName',
      className: 'transfer-page__body__list__column',
      render: text => <span className="transfer-page__body__list__column-body">{text || '-'}</span>,
    },
    {
      title: (
        <div className="transfer-page__body__list__column-header">
          {t('cms.referral.transfer.list.column.passport_last_name')}
        </div>
      ),
      dataIndex: 'description.passportLastName',
      key: 'passportLastName',
      className: 'transfer-page__body__list__column',
      render: text => <span className="transfer-page__body__list__column-body">{text || '-'}</span>,
    },
    {
      title: (
        <div className="transfer-page__body__list__column-header">
          {t('cms.referral.transfer.list.column.opp_created_time')}
        </div>
      ),
      dataIndex: 'opportunity.createdAt',
      key: 'oppoCreatedAt',
      className: 'transfer-page__body__list__column',
      render: text => (
        <span className="transfer-page__body__list__column-body">
          {formatDay(dayFormatType.UKWITHHM, text) || '-'}
        </span>
      ),
    },
    {
      title: (
        <div className="transfer-page__body__list__column-header">
          {t('cms.referral.transfer.list.column.destination_city')}
        </div>
      ),
      dataIndex: 'description.destinationCity',
      key: 'destinationCity',
      className: 'transfer-page__body__list__column',
      render: text => <span className="transfer-page__body__list__column-body">{text || '-'}</span>,
    },
    {
      title: (
        <div className="transfer-page__body__list__column-header">
          {t('cms.referral.payout.list.column.trigger_point')}
        </div>
      ),
      dataIndex: 'description.payoutStage',
      key: 'payoutStage',
      className: 'transfer-page__body__list__column',
      render: text => (
        <span className="transfer-page__body__list__column-body">
          {text
            ? t(
                `cms.referral.transfer.editor.select.payout_stage.options.${PartnerPayoutRecordStage[
                  text
                ].toLowerCase()}`,
              )
            : '-'}
        </span>
      ),
    },
    {
      title: (
        <div className="transfer-page__body__list__column-header">
          {t('cms.table.column.status')}
        </div>
      ),
      dataIndex: 'status',
      key: 'status',
      className: 'transfer-page__body__list__column',
      render: text => (
        <span className="transfer-page__body__list__column-body">
          {text
            ? t(
                `cms.referral.transfer.list.column.status.${PartnerTransactionStatus[
                  text
                ].toLowerCase()}`,
              )
            : '-'}
        </span>
      ),
    },
    {
      title: (
        <div className="transfer-page__body__list__column-header">
          {t('cms.referral.transfer.list.column.operated_by')}
        </div>
      ),
      dataIndex: 'operatedByUser.fullName',
      key: 'operatedByUserFullName',
      className: 'transfer-page__body__list__column',
      render: text => <span className="transfer-page__body__list__column-body">{text || '-'}</span>,
    },
    {
      title: (
        <div className="transfer-page__body__list__column-header">
          {t('cms.referral.transfer.list.column.operated_time')}
        </div>
      ),
      dataIndex: 'createdAt',
      key: 'createdAt',
      className: 'transfer-page__body__list__column',
      render: text => (
        <span className="transfer-page__body__list__column-body">
          {formatDay(dayFormatType.UKWITHHM, text) || '-'}
        </span>
      ),
    },
  ];

  const handleDownloadTemplate = () => {
    const fileName = 'commission transfer template.xlsx';
    downloadExcelFile(fileName);
  };

  const handleTransferPromise = func => {
    setShowTransferResult(true);
    transferPromiseFunc.current = func;
  };

  const handleOnCloseTransferResult = () => {
    setShowTransferResult(false);
    transferPromiseFunc.current = null;
  };

  const shouldShowBlankTip = transactionList.length === 0 && !isValidFilter && !loading;

  return (
    <>
      <div className="transfer-page">
        <div className="transfer-page__header">
          <h1>{t('cms.referral.transfer.title')}</h1>
          <If condition={!shouldShowBlankTip}>
            <div className="transfer-page__header__control-area">
              <button
                type="button"
                className={classNames(
                  'transfer-page__header__button transfer-page__header__button--default',
                )}
                onClick={handleDownloadTemplate}
              >
                <Icon type="cloud-download" className="transfer-page__header__button__icon" />
                {t('cms.reconciliation.booking.download_template.button')}
              </button>
              <button
                type="button"
                className={classNames(
                  'transfer-page__header__button transfer-page__header__button--default',
                )}
                onClick={() => {
                  setShowUploadModal(true);
                }}
              >
                <Icon type="upload" className="transfer-page__header__button__icon" />
                {t('cms.referral.transfer.button.upload')}
              </button>
              <button
                type="button"
                className="transfer-page__header__button transfer-page__header__button--primary"
                onClick={handleAddNewTransfer}
              >
                <Icon type="plus" className="transfer-page__header__button__icon" />
                {t('cms.referral.transfer.button.add')}
              </button>
            </div>
          </If>
        </div>

        <section
          className={classNames('transfer-page__body', {
            'transfer-page__body--bg-white': transactionList.length === 0 && !loading,
          })}
        >
          <Choose>
            <When condition={!shouldShowBlankTip}>
              <Table
                rowKey={record => record.id}
                columns={tableColumns}
                className="table-list transfer-page__body__list"
                dataSource={transactionList}
                pagination={{
                  current: pagination.pageNumber,
                  pageSize: pagination.pageSize,
                  showSizeChanger: true,
                  hideOnSinglePage: false,
                  total: listTotalCount,
                }}
                scroll={{ x: '180%' }}
                loading={loading}
                onChange={newestPagination => {
                  setPagination({
                    pageNumber: newestPagination.current,
                    pageSize: newestPagination.pageSize,
                  });
                }}
              />
            </When>
            <When condition={shouldShowBlankTip}>
              <div className="transfer-page__blank-tips">
                <div className="transfer-page__blank-tips__content">
                  <div>
                    <PicEmptyTransferIcon />
                  </div>
                  <span className="transfer-page__blank-tips__tip">
                    {t('cms.referral.transfer.blanktip')}
                  </span>
                </div>
              </div>
            </When>
          </Choose>
        </section>

        <If condition={showTransferEditor}>
          <TransferEditorContainer
            activeModal
            t={t}
            type={selectedEditorType}
            defaultTransferForm={Array.isArray(showTransferEditor) ? showTransferEditor : null}
            handleCloseModal={handleCloseEditorModal}
            handleStartProcess={handleTransferPromise}
          />
        </If>
        <If condition={showTransferResult && transferPromiseFunc.current}>
          <BulkOperationResultContainer
            activeModal
            t={t}
            label={{
              success: t('cms.referral.transfer.upload.result.success'),
              glitch: t('cms.referral.transfer.upload.result.glitch'),
              failed: t('cms.referral.transfer.upload.result.failed'),
            }}
            handlePromise={transferPromiseFunc.current}
            handleCloseModal={handleOnCloseTransferResult}
            onSuccess={handleOnCloseTransferResult}
            glitchDetail={response => {
              console.log(response);
            }}
          />
        </If>
        <If condition={showUploadModal}>
          <BulkUpdate
            onClose={() => {
              setShowUploadModal(false);
              setSelectEditorType('edit');
            }}
            onSuccess={res => {
              setShowUploadModal(false);
              setSelectEditorType('upload');
              setShowTransferEditor(res);
            }}
          />
        </If>
      </div>
    </>
  );
};

export default Transfer;
